import AddIcon from "@mui/icons-material/AddRounded";
import ExportIcon from "@mui/icons-material/OutputRounded";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import saveAs from "file-saver";
import { useCallback, useMemo, useState } from "react";
import { getErrorMessage, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { JobProgress } from "@entrilia/shared/api/types";
import AsyncJobProgress from "@entrilia/shared/components/AsyncJobProgress";
import AutocompleteCreatable from "@entrilia/shared/components/AutocompleteCreatable";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import { formatDate } from "@entrilia/shared/utilities/dateUtils";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../../api/adminApi";
import type { Fundraising, FundraisingAccessItem } from "../../../../../../api/types/fundraisingTypes";
import type { SelectOption } from "../../../../../common/filters/filterTypes";
import useFundraisingDetailsPageContext from "../useFundraisingDetailsPageContext";
import AddFundraisingAccessDialog from "./AddFundraisingAccessDialog";
import CreateFundDialog from "./CreateFundDialog";
import EditFundraisingAccessDialog from "./EditFundraisingAccessDialog";
import FundraisingAccessGrid from "./FundraisingAccessGrid";
import { FundraisingAccessGridContextProvider } from "./FundraisingAccessGridContext";
import RemoveFundraisingAccessDialog from "./RemoveFundraisingAccessDialog";

interface DialogState {
  openDialog?: "create_fund" | "edit_access" | "add_access" | "remove_access";
  editedAccessItem?: FundraisingAccessItem;
}

const applySearchFilter = (searchValue: string, allRows: FundraisingAccessItem[]) => {
  const searchTerm = searchValue.toLowerCase().trim();
  if (searchTerm.length === 0) {
    return allRows;
  }

  return allRows.filter(
    (row) =>
      row.investorName.toLowerCase().includes(searchTerm) ||
      row.contactName.toLowerCase().includes(searchTerm) ||
      (row.contactEmail ?? "").toLowerCase().includes(searchTerm)
  );
};

const updateFundraising = withErrorHandling(adminApi.updateFundraising);

const createSelectedOption = (fundraising: Fundraising) =>
  fundraising.fund ? { value: fundraising.fund.id, label: fundraising.fund.name } : null;

const emptyAccessRows: FundraisingAccessItem[] = [];

const FundraisingAccessConfig = () => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const { fundraisingCategories, isContentEditable, fundraising, onUpdated, onConfirmSave, refreshFundraising } =
    useFundraisingDetailsPageContext();

  const [selectedFund, setSelectedFund] = useState<SelectOption | null>(() => createSelectedOption(fundraising));
  const selectedFundId = selectedFund?.value;

  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [dialogState, setDialogState] = useState<DialogState>({});
  const [searchValue, setSearchValue] = useState("");

  const isAccessRowsFetchDisabled = fundraisingCategories.length === 0 || selectedFundId === undefined;
  const getAccessRows = useCallback(() => adminApi.getFundraisingAccess(defined(selectedFundId)), [selectedFundId]);

  const getFundraisings = useCallback(() => adminApi.getFundraisings(["Draft", "Preview", "Live"]), []);

  const getFunds = useCallback(
    async () => adminApi.searchFunds({ fieldIds: ["Stage"], includeInvestorCount: false }),
    []
  );

  const [accessRows, accessRowsError, { isFetching: isFetchingAccessRows, fetch: fetchAccessRows }] = useFetch(
    getAccessRows,
    { disabled: isAccessRowsFetchDisabled }
  );

  const [fundraisings, fundraisingsError, { isFetching: isFetchingFundraisings }] = useFetch(getFundraisings);

  const [fundsResp, fundsError, { isFetching: isFetchingFunds, setData: setFunds }] = useFetch(getFunds);

  const effectiveAccessRows = isAccessRowsFetchDisabled ? emptyAccessRows : (accessRows ?? emptyAccessRows);
  const filteredRows = useMemo(
    () => applySearchFilter(searchValue, effectiveAccessRows),
    [searchValue, effectiveAccessRows]
  );

  const usedFundIds = useMemo(
    () => (fundraisings ?? []).filter((f) => f.id !== fundraising.id).map((r) => r.fundId),
    [fundraising.id, fundraisings]
  );

  const fundOptions = useMemo(() => {
    if (!fundsResp?.items) {
      return [];
    }

    return (fundsResp?.items ?? [])
      .filter((f) => f.fieldValues["Stage"] === "Fundraising" && !usedFundIds.includes(f.id))
      .map((f) => ({ value: f.id, label: f.name }))
      .sort(stringComparerBy((f) => f.label));
  }, [fundsResp?.items, usedFundIds]);

  const handleJobFinished = useCallback(
    async (jobProgress: JobProgress) => {
      if (!jobProgress.isSuccess) {
        sendNotificationError(`Fund creation error. ${jobProgress.errorMessage}`);
      }

      refreshFundraising();
      fetchAccessRows();
    },
    [fetchAccessRows, refreshFundraising, sendNotificationError]
  );

  const { jobProgress, startJobProgressCheck } = useJobProgress(adminApi.getFundraisingJobProgress, {
    onJobFinished: handleJobFinished,
  });

  const handleSearch = (searchValue: string) => {
    setSearchValue(searchValue);
  };

  const save = async () => {
    setIsSaving(true);
    const [updatedFundraising, error] = await updateFundraising(fundraising.id, {
      expectedEtag: fundraising.etag,
      fundId: selectedFund?.value,
    });
    setIsSaving(false);

    if (error) {
      logError(error, "[FundraisingAccessConfig]");
      sendNotificationError(error.message);
    } else {
      sendNotification("Fundraising updated successfully");
      onUpdated(updatedFundraising);
    }
  };

  const handleSave = () => onConfirmSave(save);

  const handleReset = () => {
    setSelectedFund(createSelectedOption(fundraising));
  };

  const handleSavedWithNewFund = (updatedFundraising: Fundraising) => {
    setFunds((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedFund = updatedFundraising.fund;
      if (!updatedFund) {
        return prev;
      }

      return {
        ...prev,
        items: [...prev.items, { id: updatedFund.id, name: updatedFund.name, fieldValues: { Stage: "Fundraising" } }],
      };
    });

    setSelectedFund(createSelectedOption(updatedFundraising));

    onUpdated(updatedFundraising);
  };

  const handleEditAccess = (editedAccessItem: FundraisingAccessItem) => {
    setDialogState({ openDialog: "edit_access", editedAccessItem });
  };

  const handleRemoveAccess = (editedAccessItem: FundraisingAccessItem) => {
    setDialogState({ openDialog: "remove_access", editedAccessItem });
  };

  const handleAddAccess = () => {
    setDialogState({ openDialog: "add_access" });
  };

  const handleExportToExcelClick = async () => {
    const fundId = selectedFund?.value;
    if (!fundId) {
      return;
    }

    const fileName = `${fundraising.name}_Contact_Access_${formatDate(new Date(), "yyyyMMdd")}.xlsx`;

    setIsExporting(true);
    try {
      const resp = await adminApi.getFundraisingAccessByFundIdAsExcelFile(fundId, fileName);
      if (!resp.data) {
        setIsExporting(false);
        sendNotificationError("Failed to export access rows");
        return;
      }

      const contentType = (resp.headers["content-type"] as string) || "application/octet-stream";
      saveAs(new Blob([resp.data], { type: contentType }), fileName);
    } catch (error) {
      logError(error, "[FundraisingAccessConfig] getFundraisingAccessByFundIdAsExcelFile");
      sendNotificationError(getErrorMessage(error) || "Failed to export access rows");
    } finally {
      setIsExporting(false);
    }
  };

  if (jobProgress) {
    return <AsyncJobProgress title="Please wait, investor and contacts are being copied" jobProgress={jobProgress} />;
  }

  const isEdited = selectedFund?.value !== fundraising.fund?.id;
  const isValid = fundraising.status === "Draft" || Boolean(selectedFund);
  const showValidationError = isEdited && !isValid;

  const fetchError = (isAccessRowsFetchDisabled ? undefined : accessRowsError) || fundraisingsError || fundsError;
  const isFetching = isFetchingAccessRows || isFetchingFunds || isFetchingFundraisings;

  const isFundSelectionDisabled =
    !isContentEditable || fundraisingCategories.length === 0 || ["Live", "Completed"].includes(fundraising.status);

  return (
    <Stack spacing={2.5} sx={{ flex: 1, pt: 4 }}>
      <Box>
        <AutocompleteCreatable
          withLazyRendering
          noItemsText="No funds found"
          onCreateOption={() => setDialogState({ openDialog: "create_fund" })}
          openOnFocus
          fullWidth
          disabled={isFundSelectionDisabled}
          options={fundOptions}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Fund"
              error={showValidationError}
              helperText={showValidationError ? "Fund is required" : ""}
            />
          )}
          value={selectedFund}
          onChange={(_, newValue) => {
            if (newValue) {
              setSelectedFund(newValue);
            }
          }}
        />

        {isContentEditable && (
          <Stack direction="row" spacing={1} sx={{ pt: 2 }}>
            <Button variant="contained" loading={isSaving} onClick={handleSave} disabled={!isEdited || !isValid}>
              Save
            </Button>
            <Button variant="text" color="secondary" onClick={handleReset} disabled={!isEdited}>
              Cancel
            </Button>
          </Stack>
        )}
      </Box>
      <FundraisingAccessGridContextProvider onEditAccess={handleEditAccess} onRemoveAccess={handleRemoveAccess}>
        <Stack spacing={2} sx={{ flex: 1, pb: 2 }}>
          <Stack spacing={0.5} sx={{ pt: 2 }}>
            <Typography variant="subtitle1">Investors/Contacts</Typography>
            <Typography color="textSecondary">
              The list of investor contacts who can access the fundraising is determined by the selected fund, portal
              role, and assigned access categories.
            </Typography>
          </Stack>

          <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <RecordCounter records={filteredRows.length} totalRecords={effectiveAccessRows.length} hide={isFetching} />
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <SearchField
                initialValue={searchValue}
                onSearch={handleSearch}
                debounceTimeMs={300}
                disabled={isFetching || effectiveAccessRows.length === 0}
              />
              <Button
                loading={isExporting}
                color="secondary"
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={handleExportToExcelClick}
                disabled={isFetching || effectiveAccessRows.length === 0 || !selectedFundId}
              >
                Export to Excel
              </Button>
              {isContentEditable && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddAccess}
                  disabled={isFetching || !selectedFund}
                >
                  Add New
                </Button>
              )}
            </Stack>
          </Box>
          {!fetchError && <FundraisingAccessGrid rows={filteredRows} isLoading={isFetching} />}
          {fetchError && <DataLoadingFailed />}
        </Stack>
      </FundraisingAccessGridContextProvider>
      {dialogState.openDialog === "create_fund" && (
        <CreateFundDialog
          allFunds={fundsResp?.items ?? []}
          onSubmit={handleSavedWithNewFund}
          onAsyncOperationStart={startJobProgressCheck}
          onClose={() => setDialogState({})}
        />
      )}
      {dialogState.openDialog === "edit_access" && dialogState.editedAccessItem && selectedFund?.value && (
        <EditFundraisingAccessDialog
          editedAccessItem={dialogState.editedAccessItem}
          onSubmit={fetchAccessRows}
          onClose={() => setDialogState({})}
        />
      )}
      {dialogState.openDialog === "remove_access" &&
        dialogState.editedAccessItem &&
        selectedFundId &&
        effectiveAccessRows.length > 0 && (
          <RemoveFundraisingAccessDialog
            editedAccessItem={dialogState.editedAccessItem}
            fundInvestorsWithContactCount={
              effectiveAccessRows.filter((r) => r.contactId === dialogState.editedAccessItem?.contactId).length
            }
            onSubmit={fetchAccessRows}
            onClose={() => setDialogState({})}
          />
        )}
      {dialogState.openDialog === "add_access" && selectedFundId && (
        <AddFundraisingAccessDialog
          currentAccessAssignments={effectiveAccessRows}
          fundId={selectedFundId}
          onSubmit={fetchAccessRows}
          onClose={() => setDialogState({})}
        />
      )}
    </Stack>
  );
};

export default FundraisingAccessConfig;
